import { Injectable } from '@angular/core';
import { SceneUtilsService } from '../../services/scene-utils.service';
import { KeyStateService } from '../../services/key-state.service';
import { CameraService } from '../../services/camera.service';
import { StageService } from '../../services/stage.service';
import { ComponentBase } from '../../component-base';
import {
  CANVAS_USES_POINTER_EVENTS,
  patchPointerOffsets,
  centroidFromPoints
} from '../../utils/pointer-input.utils';

declare let THREE: any;

const PAN_CAMERA_Y = 500;

/** Pan 3+ пальца: Touch при `!CANVAS_USES_POINTER_EVENTS`; pointer map — при pointer-only canvas. */
@Injectable()
export class MoveCameraService extends ComponentBase {
  private clickPosition: any;
  private raycaster = new THREE.Raycaster(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, -1, 0));
  private pointers = new Map<number, { x: number; y: number }>();
  private threeTouchCenterPx: { x: number; y: number } | null = null;

  constructor(
    private sceneUtils: SceneUtilsService,
    private keyState: KeyStateService,
    private cameraManager: CameraService,
    private stage: StageService
  ) {
    super();
    this.clickPosition = null;
  }

  private getPointersCenter(): { x: number; y: number } | null {
    if (!this.pointers || this.pointers.size < 1) {
      return null;
    }
    return centroidFromPoints(Array.from(this.pointers.values()));
  }

  mouseDown(e: any) {
    if (e && e.pointerType === 'touch' && typeof e.pointerId === 'number') {
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size >= 3) {
        const center = this.getPointersCenter();
        this.threeTouchCenterPx = center;
        return;
      }
      if (this.pointers.size === 2) {
        return;
      }
      patchPointerOffsets(e, (e.currentTarget || e.target) as EventTarget);
    }

    this.clickPosition = this.sceneUtils.screenToWorld(e.offsetX, e.offsetY);
    this.clickPosition.y = PAN_CAMERA_Y;
    this.raycaster.ray.origin.copy(this.clickPosition);
    const children = this.stage.getChildren();
    this.raycaster.intersectObjects(children, true);
  }

  mouseMove(e: any) {
    if (e && e.pointerType === 'touch' && typeof e.pointerId === 'number') {
      if (!this.pointers.has(e.pointerId)) {
        return;
      }
      if (e.preventDefault) {
        e.preventDefault();
      }
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size >= 3 && this.threeTouchCenterPx !== null) {
        const center = this.getPointersCenter();
        if (!center) {
          return false;
        }
        const camera = this.cameraManager.camera;
        const scaleX = camera && camera.scale && camera.scale.x ? camera.scale.x : 1;
        const dx = center.x - this.threeTouchCenterPx.x;
        const dy = center.y - this.threeTouchCenterPx.y;
        camera.position.x += -dx / scaleX;
        camera.position.z += -dy / scaleX;
        if (camera && camera.position) {
          camera.position.y = PAN_CAMERA_Y;
        }
        camera.updateMatrixWorld();
        this.threeTouchCenterPx = center;
        this.fireEvent('move');
        return false;
      }
      if (this.pointers.size === 2) {
        return;
      }
      patchPointerOffsets(e, (e.currentTarget || e.target) as EventTarget);
    }

    if (this.keyState.result.isMouseHold && this.clickPosition !== null) {
      const currentMousePosition = this.sceneUtils.screenToWorld(e.offsetX, e.offsetY);
      const camera = this.cameraManager.camera;
      const delta = this.clickPosition.clone().sub(currentMousePosition);
      camera.position.add(delta);
      camera.updateMatrixWorld();
      this.fireEvent('move');
      return false;
    }
  }

  mouseUp(e?: any) {
    if (e && typeof e.pointerId === 'number') {
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 3) {
        this.threeTouchCenterPx = null;
      }
    } else {
      this.pointers.clear();
      this.threeTouchCenterPx = null;
    }
    this.clickPosition = null;
  }

  touchStart(e: TouchEvent) {
    if (CANVAS_USES_POINTER_EVENTS) {
      return;
    }
    if (!e || !e.targetTouches || e.targetTouches.length < 3) {
      this.threeTouchCenterPx = null;
      return;
    }
    const xs: number[] = [];
    const ys: number[] = [];
    for (let i = 0; i < e.targetTouches.length; i++) {
      xs.push(e.targetTouches[i].clientX);
      ys.push(e.targetTouches[i].clientY);
    }
    this.threeTouchCenterPx = {
      x: xs.reduce((a, b) => a + b, 0) / xs.length,
      y: ys.reduce((a, b) => a + b, 0) / ys.length
    };
  }

  touchMove(e: TouchEvent) {
    if (CANVAS_USES_POINTER_EVENTS) {
      return;
    }
    if (!e || !e.targetTouches || e.targetTouches.length < 3 || this.threeTouchCenterPx === null) {
      return;
    }
    e.preventDefault();
    const xs: number[] = [];
    const ys: number[] = [];
    for (let i = 0; i < e.targetTouches.length; i++) {
      xs.push(e.targetTouches[i].clientX);
      ys.push(e.targetTouches[i].clientY);
    }
    const center = {
      x: xs.reduce((a, b) => a + b, 0) / xs.length,
      y: ys.reduce((a, b) => a + b, 0) / ys.length
    };
    const camera = this.cameraManager.camera;
    const scaleX = camera && camera.scale && camera.scale.x ? camera.scale.x : 1;
    const dx = center.x - this.threeTouchCenterPx.x;
    const dy = center.y - this.threeTouchCenterPx.y;
    camera.position.x += -dx / scaleX;
    camera.position.z += -dy / scaleX;
    if (camera && camera.position) {
      camera.position.y = PAN_CAMERA_Y;
    }
    camera.updateMatrixWorld();
    this.threeTouchCenterPx = center;
    this.fireEvent('move');
    return false;
  }

  touchEnd() {
    if (CANVAS_USES_POINTER_EVENTS) {
      return;
    }
    this.threeTouchCenterPx = null;
  }
}



// WEBPACK FOOTER //
// ./src/app/Component(2D and 3D)/2D/move-camera.service.ts